import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Link2, Clock, Loader2 } from 'lucide-react';

interface AddReferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (reference: NewReference) => Promise<void>;
  timestamp: number;
  isDarkMode: boolean;
}

export interface NewReference {
  startTime: number;
  endTime: number;
  name: string;
  category?: string;
  linkText?: string;
  url?: string;
  context?: string;
  visualSuggestion?: string;
}

// Helper to format seconds to TTML timestamp format
const formatTimestamp = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.round((seconds % 1) * 1000);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
};

// Helper to format for display
const formatDisplayTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const AddReferenceModal: React.FC<AddReferenceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  timestamp,
  isDarkMode
}) => {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [linkText, setLinkText] = useState('');
  const [url, setUrl] = useState('');
  const [context, setContext] = useState('');
  const [visualSuggestion, setVisualSuggestion] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default end time is 30 seconds after start
  const endTime = timestamp + 30;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Reference name is required');
      return;
    }

    // Validate URL if provided
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      setError('URL must start with http:// or https://');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        startTime: timestamp,
        endTime: endTime,
        name: name.trim(),
        category: category.trim() || undefined,
        linkText: linkText.trim() || undefined,
        url: url.trim() || undefined,
        context: context.trim() || undefined,
        visualSuggestion: visualSuggestion.trim() || undefined
      });

      // Reset form and close
      setName('');
      setCategory('');
      setLinkText('');
      setUrl('');
      setContext('');
      setVisualSuggestion('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save reference');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setName('');
      setCategory('');
      setLinkText('');
      setUrl('');
      setContext('');
      setVisualSuggestion('');
      setError(null);
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg w-full max-w-lg shadow-xl overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
              <div className="flex items-center gap-2">
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgb(236, 72, 153)' }}
                >
                  <Link2 size={14} className="text-white" />
                </div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Add Reference
                </h3>
              </div>
              <button
                onClick={handleClose}
                disabled={isSaving}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                    : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                }`}
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Timestamp display */}
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
              }`}>
                <Clock size={16} className="text-[#B57700] dark:text-[#FFCF74]" />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Timestamp: {formatDisplayTime(timestamp)}
                </span>
                <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  ({formatTimestamp(timestamp)})
                </span>
              </div>

              {/* Error message */}
              {error && (
                <div className="px-3 py-2 rounded-lg bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {/* Name field - required */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Reference name..."
                  disabled={isSaving}
                  className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                    isDarkMode
                      ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Category and Link Text row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Category
                  </label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="Person, Paper, Book..."
                    disabled={isSaving}
                    className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                      isDarkMode
                        ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Link Text
                  </label>
                  <input
                    type="text"
                    value={linkText}
                    onChange={(e) => setLinkText(e.target.value)}
                    placeholder="arXiv Paper, Website..."
                    disabled={isSaving}
                    className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                      isDarkMode
                        ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                </div>
              </div>

              {/* URL field */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  URL
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://..."
                    disabled={isSaving}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                      isDarkMode
                        ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                  />
                  {url && url.startsWith('http') && (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-lg p-2 text-[#B57700] transition-colors hover:bg-[#FFF4D8] hover:text-[#8B5B00] dark:hover:bg-[#232E3F]/40 dark:text-[#FFCF74]"
                      title="Open URL"
                    >
                      <ExternalLink size={18} />
                    </a>
                  )}
                </div>
              </div>

              {/* Context field */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Context
                </label>
                <textarea
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder="Why this reference is relevant..."
                  rows={2}
                  disabled={isSaving}
                  className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] resize-y ${
                    isDarkMode
                      ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Visual Suggestion field */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Visual Suggestion
                </label>
                <input
                  type="text"
                  value={visualSuggestion}
                  onChange={(e) => setVisualSuggestion(e.target.value)}
                  placeholder="Photo, diagram, book cover..."
                  disabled={isSaving}
                  className={`w-full px-3 py-2 rounded-lg border transition-colors focus:ring-2 focus:ring-[#FFB52B]/35 focus:border-[#FFB52B] ${
                    isDarkMode
                      ? 'bg-gray-900 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSaving}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving || !name.trim()}
                  className="flex items-center gap-2 rounded-lg bg-[#FFB52B] px-4 py-2 font-medium text-[#232E3F] transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[#FFC54D]"
                >
                  {isSaving ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Add Reference'
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddReferenceModal;
